import { HorizontalSpacer } from "../../atoms/Spacers";
import { useState } from "react";
import ExpandAndCollapseTitleSection from "../../molecules/ExpandAndCollapseTitleSection";
import type {
  InvoiceRowModel,
  InvoiceValidationModel,
} from "../../../models/InvoiceModels";
import InvoiceIssuerDetailsSection from "./invoiceDetailsSections/InvoiceIssuerDetailsSection";
import InvoiceRecipientDetailsSection from "./invoiceDetailsSections/InvoiceRecipientDetailsSection";
import InvoiceDetailsDetailsSection from "./invoiceDetailsSections/InvoiceDetailsDetailsSection";
import InvoiceRowsDetailsSection from "./invoiceDetailsSections/InvoiceRowsDetailsSection";
import Card from "../../atoms/Card";

interface Props {
  isGridView: boolean;
  validationResult?: InvoiceValidationModel;
  issuerName: string;
  issuerClientIdentifier?: string;
  issuerReference?: string;
  dueDate?: Date;
  invoiceDate?: Date;
  invoiceRecipient?: {
    name?: string;
    clientIdentifier?: string;
    recipientEmail?: string;
    address1?: string;
    address2?: string;
    city?: string;
    countryCode?: string;
    customerCode?: string;
    zipCode?: string;
    isForeignIdentifier?: boolean;
  };
  totalAmountWithVat?: number;
  invoiceNumber: string;
  totalVatAmount?: number;
  shippingCost?: number;
  rounding?: number;
  invoiceRows?: Array<InvoiceRowModel>;
  currencyCode?: string;
  isEditable: boolean;
  shippingFeeVatPercentage?: number;
  invoiceFee?: number;
  isCreditInvoice?: boolean;
  vatReverseCharge?: boolean;
}

const recipientFieldsKeys = [
  "RecipientName",
  "RecipientAddressLine1",
  "RecipientAddressLine2",
  "RecipientCity",
  "RecipientPostalCode",
  "RecipientEmail",
  "RecipientNumber",
  "RecipientClientIdentifier",
];

const InvoiceDetailsFieldKeys = [
  "InvoiceNumber",
  "InvoiceDate",
  "DueDate",
  "TotalAmountExcludingVat",
  "TotalAmountIncludingVat",
  "TotalVatAmount",
  "VatTotalsMatch",
  "ShippingFeeAmount",
  "CurrencyCode",
  "InvoiceFeeAmount",
];

const InvoiceDetailsColumn: React.FC<Props> = ({
  isGridView,
  validationResult,
  issuerName,
  issuerClientIdentifier,
  dueDate,
  invoiceDate,
  invoiceRecipient,
  totalAmountWithVat,
  invoiceNumber,
  issuerReference,
  totalVatAmount,
  shippingCost,
  rounding,
  invoiceRows,
  currencyCode,
  isEditable,
  shippingFeeVatPercentage,
  invoiceFee,
  isCreditInvoice,
  vatReverseCharge,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const recipientFields = validationResult?.fieldValidations.filter((field) =>
    recipientFieldsKeys.includes(field.fieldType),
  );
  const invoiceDetailsFields = validationResult?.fieldValidations.filter(
    (field) => InvoiceDetailsFieldKeys.includes(field.fieldType),
  );

  return (
    <div
      style={{
        flex: isGridView ? (isExpanded ? 1 : "0 0 auto") : undefined,
        width: isGridView ? undefined : "100%",
        minWidth: 0,
      }}
    >
      <ExpandAndCollapseTitleSection
        isExpanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        labelKey="invoices.invoiceDetails"
      />
      {isExpanded && (
        <>
          <HorizontalSpacer spacing={1} />

          <Card>
            <InvoiceIssuerDetailsSection
              issuerName={issuerName}
              issuerClientIdentifier={issuerClientIdentifier}
              issuerReference={issuerReference}
              invoiceId={validationResult?.invoiceId ?? ""}
              isEditable={isEditable}
            />
          </Card>
          <HorizontalSpacer spacing={1.5} />

          <Card dontClipOverflow>
            <InvoiceRecipientDetailsSection
              recipientFields={recipientFields ?? []}
              invoiceId={validationResult?.invoiceId ?? ""}
              invoiceRecipient={invoiceRecipient}
              isEditable={isEditable}
            />
          </Card>

          <HorizontalSpacer spacing={1.5} />

          <Card dontClipOverflow>
            <InvoiceDetailsDetailsSection
              invoiceFields={invoiceDetailsFields ?? []}
              invoiceId={validationResult?.invoiceId ?? ""}
              dueDate={dueDate}
              invoiceDate={invoiceDate}
              totalAmountWithVat={totalAmountWithVat}
              invoiceNumber={invoiceNumber}
              totalVatAmount={totalVatAmount}
              shippingCost={shippingCost}
              shippingFeeVatPercentage={shippingFeeVatPercentage}
              rounding={rounding}
              currencyCode={currencyCode}
              isEditable={isEditable}
              invoiceFee={invoiceFee}
              isCreditInvoice={isCreditInvoice}
              vatReverseCharge={vatReverseCharge}
            />
          </Card>

          <HorizontalSpacer spacing={1.5} />

          <Card>
            <InvoiceRowsDetailsSection
              invoiceRowsValidation={validationResult?.rowValidations ?? []}
              invoiceRows={invoiceRows ?? []}
              invoiceId={validationResult?.invoiceId ?? ""}
              totalAmountWithVat={totalAmountWithVat ?? 0}
              rounding={rounding ?? 0}
              shippingCost={shippingCost ?? 0}
              shippingFeeVatPercentage={shippingFeeVatPercentage ?? 0}
              invoiceFee={invoiceFee ?? 0}
              isEditable={isEditable}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default InvoiceDetailsColumn;
